import * as VisuallyHiddenPrimitive from '@radix-ui/react-visually-hidden';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Input } from '../../../../design-system/input/input';
import { Modal, ModalContent, ModalDescription, ModalFooter, ModalTitle } from '../../../../design-system/modal/modal';
import { Stack } from '../../../../design-system/stack/stack';
import { CategoryView } from '../../../../types/entity-types';
import { cn } from '../../../../utils/cn';

type Level1 = CategoryView['level1'];

type Level1CategoryChangeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectLevel1Category: (categoryId: string) => void;
  level1List: Level1[];
  rowLevel1?: Level1;
};

export const Level1CategoryChangeModal = ({
  isOpen,
  onClose,
  onSelectLevel1Category,
  level1List,
  rowLevel1,
}: Level1CategoryChangeModalProps) => {
  const [search, setSearch] = useState('');
  // The row level1 needs to be selected by default
  const [selectedLevel1, setSelectedLevel1] = useState<string | undefined>(rowLevel1);
  const [isLoading, setIsLoading] = useState(false);

  const filteredLevel1Categories = level1List.filter((c) => c.toLowerCase().includes(search.toLowerCase()));

  return (
    <Modal open={isOpen} onOpenChange={onClose}>
      <ModalContent className="h-[600px] w-[400px]">
        <VisuallyHiddenPrimitive.Root asChild>
          <div>
            <ModalTitle>Select category</ModalTitle>

            <ModalDescription>Modal to change the category assigned to a sub-category</ModalDescription>
          </div>
        </VisuallyHiddenPrimitive.Root>

        <Stack align="stretch" justify="between" className="h-full">
          <div className="p-4">
            <Input
              leftIcon={<Search size={16} />}
              placeholder="Search categories"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              clearable
              onClear={() => setSearch('')}
            />
          </div>

          <div className="relative h-full min-h-0">
            <Stack className="absolute inset-0 overflow-auto">
              {filteredLevel1Categories.map((l1) => {
                return (
                  <Button
                    key={l1}
                    variant="ghost"
                    rounded={false}
                    size="none"
                    className="w-full *:w-full"
                    onClick={() => {
                      if (selectedLevel1 === l1) {
                        setSelectedLevel1(undefined);

                        return;
                      }

                      setSelectedLevel1(l1);
                    }}
                  >
                    <Inline
                      justify="start"
                      className={cn('w-full p-4 hover:bg-seapoint-gray-background-hover', {
                        'bg-seapoint-yellow/20 hover:bg-seapoint-yellow/20': selectedLevel1 === l1,
                      })}
                    >
                      <p className="text-sm font-medium">{l1}</p>
                    </Inline>
                  </Button>
                );
              })}
            </Stack>
          </div>

          <ModalFooter>
            <Button
              onClick={async () => {
                if (!selectedLevel1) {
                  return;
                }

                setIsLoading(true);

                await onSelectLevel1Category(selectedLevel1);

                setIsLoading(false);
              }}
              disabled={!selectedLevel1}
              isLoading={isLoading}
              className="flex-1"
            >
              {selectedLevel1 ? `Select ${selectedLevel1}` : 'Select category'}
            </Button>

            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </Stack>
      </ModalContent>
    </Modal>
  );
};
